import { FC, ReactNode } from "react";
import { formatDurationUntilExpiration, SupportAccessGrant } from "./utils";
import {
  Alert,
  Button,
  Column,
  CopyIcon,
  Spinner,
  Text,
  useToast,
} from "@hightouch/htui";
import { useClipboard } from "use-clipboard-copy";

type Props = {
  grant?: SupportAccessGrant;
  workspaceSlug?: string | null;
  isFetching: boolean;
  additionalContent?: ReactNode;
};

export const SupportAccessGrantRequiredAlert: FC<Props> = ({
  grant,
  workspaceSlug,
  isFetching,
  additionalContent,
}) => {
  const { toast } = useToast();
  const clipboard = useClipboard({
    onSuccess: () => {
      toast({
        variant: "success",
        id: "support-grant-copy-to-clipboard",
        title: "Copied to clipboard",
      });
    },
  });

  return (
    <Alert
      type={grant ? "warning" : "error"}
      title="This organization requires approval for support access"
      message={
        <Column gap={4}>
          {isFetching ? (
            <Spinner />
          ) : grant ? (
            <Text>
              Temporary access has been granted{" "}
              <Text fontWeight="semibold">
                {grant.workspace_id
                  ? "for this workspace only"
                  : "for the entire organization"}
              </Text>
              . Expires in{" "}
              <Text fontWeight="semibold">
                {formatDurationUntilExpiration(grant.expires_at)}.
              </Text>
            </Text>
          ) : (
            <Column gap={4}>
              <Button
                variant="secondary"
                icon={CopyIcon}
                onClick={() =>
                  clipboard.copy(
                    `${window.location.origin}/${workspaceSlug}/settings/support-access`,
                  )
                }
              >
                Copy shareable link to approval page
              </Button>
            </Column>
          )}
          {additionalContent}
        </Column>
      }
    />
  );
};
